import { useCallback, useEffect, useMemo } from 'react';
import { useSessionContext } from '@supabase/auth-helpers-react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import storage from 'local-storage-fallback';

import { supabaseEh } from '../lib/supabase';
import type { Database } from '../types/database';
import { USER_EVENTS, useTracking } from './useTracking';
import { LS_SIGNOUT_DATE_KEY } from '@/lib/constants';

import type { User } from '@supabase/supabase-js';
import { getAffiliateRef } from '@/lib/affiliates';
import { EHUserDetailsMeta } from '@/lib/types';
import { SUBSCRIPTION_PLANS } from '@/lib/ehPrices';

// Types for the user details and related data
type Character = Database['eh']['Tables']['characters']['Row'];
type Chat = Database['eh']['Tables']['chats']['Row'];
type UserDetails = Database['eh']['Tables']['user_details']['Row'];
type Subscriptions = Database['eh']['Tables']['subscriptions']['Row'];
type Settings = Database['eh']['Tables']['settings']['Row'];

// Type for user details with relations from the query
type UserDetailsWithRelations = UserDetails & {
  chats?: Pick<Chat, 'id' | 'character_id'>[];
  subscriptions?: Subscriptions[];
  settings?: Settings | null;
};

export interface UserInfo {
  characters: Pick<Character, 'id' | 'url_slug' | 'first_name' | 'last_name' | 'avatar_fpath'
    | 'profile_media_fpaths' | 'parent_char_id' | 'character_scope' | 'chat_starter_im' | 'chat_starter_rp' >[];
  chats: Pick<Chat, 'id' | 'character_id'>[];
  user: User | null;
  userDetails: UserDetails | null;
  settings: Settings | null;
  subscriptions: Subscriptions[];
  isLoading: boolean;
  isUserDetailsLoading: boolean;
  error: string | null;
  isPastUser: boolean;
  isSubscriber: boolean;
  isRenewableSubscriber: boolean;
  isUnlimited: boolean;
  is18Confirmed: boolean;
  refetch: () => Promise<void>;
  refetchCharacters: () => Promise<UserInfo['characters']>;
  refetchUserInfo: () => Promise<UserDetailsWithRelations | null>;
  updateUserSettings: (settingsUpdate: Partial<Settings>) => Promise<Settings | null>;
  updateUserDetails: (detailsUpdate: Partial<UserDetails>) => Promise<UserDetails | null>;
}

// Query keys
const USER_QUERY_KEYS = {
  userDetails: (userId?: string) => userId ? ['userDetails', userId] : ['userDetails'],
  characters: (userId?: string) => userId ? ['userCharacters', userId] : ['userCharacters'],
} as const;

// Query functions
const fetchUserDetailsWithRelations = async (userId: string): Promise<UserDetailsWithRelations | null> => {
  const { data, error } = await supabaseEh
    .from('user_details')
    .select(`
      *,
      chats!chats_user_details_id_fkey(id, character_id),
      subscriptions!subscriptions_user_details_id_fkey(
        *
      ),
      settings!settings_id(*)
    `)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  return data;
};

const fetchUserCharacters = async (userId: string) => {
  const { data, error } = await supabaseEh
    .from('characters')
    .select(`
      id,
      url_slug,
      first_name,
      last_name,
      age,
      aboutme,
      personality,
      kinks,
      avatar_fpath,
      profile_media_fpaths,
      lqip,
      parent_char_id,
      character_scope,
      chat_starter_im,
      chat_starter_rp,
      flagged
    `)
    .order('created_at', { ascending: false })
    .limit(100) // for now, arbitrary, for performance reasons and can skip pagination until needed
    .eq('created_by', userId);

  if (error) throw error;
  return data?.filter(c => !c.flagged) || [];
};

// Utility function to extract user details from data with relations
const extractUserDetails = (data: UserDetailsWithRelations): UserDetails => {
  const { chats, subscriptions, settings, ...userDetails } = data;
  return userDetails;
};

export function useUserInfo(): UserInfo {
  const { session, isLoading: isSessionLoading } = useSessionContext();
  const { phIdentify, trackEvent } = useTracking();
  const queryClient = useQueryClient();

  const user = session?.user || null;
  const hasSession = !!user;
  const isPastUser = !!storage.getItem(LS_SIGNOUT_DATE_KEY);

  // Query for user details with relations
  const {
    data: userDetailsData,
    isLoading: isUserDetailsLoading,
    error: userDetailsError,
    refetch: refetchUserInfoQuery,
  } = useQuery({
    queryKey: USER_QUERY_KEYS.userDetails(user?.id || ''),
    queryFn: () => fetchUserDetailsWithRelations(user!.id),
    enabled: hasSession,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (error?.message?.includes('JWT') || error?.message?.includes('auth')) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Query for user characters
  const {
    data: charactersData = [],
    isLoading: isCharactersLoading,
    error: charactersError,
    refetch: refetchCharactersQuery,
  } = useQuery({
    queryKey: USER_QUERY_KEYS.characters(user?.id || ''),
    queryFn: () => fetchUserCharacters(user!.id),
    enabled: hasSession,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Mutation for updating user settings
  const updateSettingsMutation = useMutation({
    mutationFn: async (settingsUpdate: Partial<Settings>) => {
      if (!user) throw new Error('No user session');

      const { data, error } = await supabaseEh
        .from('settings')
        .update(settingsUpdate)
        .eq('user_id', user.id)
        .eq('settings_scope', 'user')
        .select('*')
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      // Update the cache
      queryClient.setQueryData(
        USER_QUERY_KEYS.userDetails(user!.id),
        (oldData: UserDetailsWithRelations | undefined) => oldData ? { ...oldData, settings: data } : oldData
      );
    },
    onError: (error) => {
      if (import.meta.env.DEV) console.error('[DEV] Failed to update user settings:', error);
      trackEvent(USER_EVENTS.USER_UPDATE_ERROR, { 
        update: 'settings', 
        error: `Failed to update settings: ${String(error)}` 
      });
    },
  });

  // Mutation for updating user details
  const updateDetailsMutation = useMutation({
    mutationFn: async (detailsUpdate: Partial<UserDetails>) => {
      if (!user) throw new Error('No user session');

      const { data, error } = await supabaseEh
        .from('user_details')
        .update(detailsUpdate)
        .eq('user_id', user.id)
        .select('*')
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      // Update the cache
      queryClient.setQueryData(
        USER_QUERY_KEYS.userDetails(user!.id),
        (oldData: UserDetailsWithRelations | undefined) => {
          if (!oldData) return oldData;
          const { chats, subscriptions, settings, ...existingUserDetails } = oldData;
          return { 
            ...existingUserDetails, 
            ...data,
            chats,
            subscriptions,
            settings
          };
        }
      );
    },
    onError: (error) => {
      if (import.meta.env.DEV) console.error('[DEV] Failed to update user details:', error);
      trackEvent(USER_EVENTS.USER_UPDATE_ERROR, { 
        update: 'user_details', 
        error: `Failed to update details: ${String(error)}` 
      });
    },
  });

  // Extract data from query results
  const userDetails = useMemo(() => 
    userDetailsData ? extractUserDetails(userDetailsData) : null,
    [userDetailsData]
  );
  const chatWatchKey = userDetailsData?.chats?.map(chat => chat.id).join(',') || '';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const chats = useMemo(() => userDetailsData?.chats || [], [chatWatchKey]);
  const subscriptionWatchKey = userDetailsData?.subscriptions
    ?.map(sub => `${sub.id}-${sub.status}-${sub.end_at}`)
    .join(',') || ''
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const subscriptions = useMemo(() => userDetailsData?.subscriptions || [], [subscriptionWatchKey]);
  const settings = useMemo(() => userDetailsData?.settings || null, [userDetailsData?.settings]);
  const characters = charactersData;

  // Computed properties
  const isSubscriber = useMemo(() => 
    subscriptions.some(sub => sub.status === 'active' && sub.end_at && new Date(sub.end_at) > new Date()),
    [subscriptions]
  );
  const isRenewableSubscriber = useMemo(() => 
    subscriptions.some(sub => sub.status === 'active' && sub.end_at && new Date(sub.end_at) > new Date()
      && SUBSCRIPTION_PLANS.filter(plan => plan.hasMonthlyMultiplePeriod).some(plan => plan.id === sub.type)),
    [subscriptions]
  );
  const isUnlimited = useMemo(() => 
    subscriptions.some(sub => sub.status === 'active' && sub.end_at && new Date(sub.end_at) > new Date() && sub.type?.includes('unlimited')),
    [subscriptions]
  );
  const is18Confirmed = useMemo(() => {
    if (!userDetails?.eh_usr_meta) return false;
    const age18ConfirmedAt = (userDetails.eh_usr_meta as EHUserDetailsMeta).age18ConfirmedAt;
    return !!age18ConfirmedAt;
  }, [userDetails]);

  const isLoading = isSessionLoading || isUserDetailsLoading || isCharactersLoading;
  const error = userDetailsError?.message || charactersError?.message || null;

  // Handle user identification on login
  useEffect(() => {
    if (userDetailsData && user?.last_sign_in_at) {
      const oneMinuteAgo = new Date(Date.now() - 1000 * 60);
      if (new Date(user.last_sign_in_at) > oneMinuteAgo) {
        phIdentify(user, {
          isSubscriber,
          ...(is18Confirmed && { is18Confirmed }),
        });
      }
    }
  }, [isSubscriber, is18Confirmed, userDetailsData, user, phIdentify]);

  // Clear queries when user logs out
  useEffect(() => {
    if (!hasSession) {
      queryClient.removeQueries({ queryKey: ['userDetails'] });
      queryClient.removeQueries({ queryKey: ['userCharacters'] });
    }
  }, [hasSession, queryClient]);

  // Track errors
  useEffect(() => {
    if (error) {
      trackEvent(USER_EVENTS.USER_ERROR, { 
        error: `Failed to fetch user info: ${error}` 
      });
    }
  }, [error, trackEvent]);

  // Refetch function that refetches both queries
  const refetch = useCallback(async () => {
    await Promise.all([
      refetchUserInfoQuery(),
      refetchCharactersQuery(),
    ]);
  }, [refetchUserInfoQuery, refetchCharactersQuery]);

  // User info refetch function
  const refetchUserInfo = useCallback(async () => {
    queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.userDetails() });
    const result = await refetchUserInfoQuery();
    return result.data || null;
  }, [queryClient, refetchUserInfoQuery]);

  // Characters refetch function
  const refetchCharacters = useCallback(async () => {
    queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.characters() });
    const result = await refetchCharactersQuery();
    return result.data || [];
  }, [queryClient, refetchCharactersQuery]);

  // Update functions with error handling
  const updateUserSettings = useCallback(async (settingsUpdate: Partial<Settings>): Promise<Settings | null> => {
    if (!user) {
      if (import.meta.env.DEV) console.error('[DEV] Cannot update settings: no user session');
      trackEvent(USER_EVENTS.USER_UPDATE_ERROR, { update: 'settings', error: 'No user session' });
      return null;
    }
    if (!userDetails?.settings_id) {
      if (import.meta.env.DEV) console.warn('[DEV] No settings_id in hook when trying to update');
      trackEvent(USER_EVENTS.USER_UPDATE_ERROR, { update: 'settings', error: 'Warning: No settings_id (non-blocking)' });
    }

    try {
      const result = await updateSettingsMutation.mutateAsync(settingsUpdate);
      return result;
    } catch (error) {
      trackEvent(USER_EVENTS.USER_UPDATE_ERROR, {
        update: 'settings',
        error: error instanceof Error ? error.message : JSON.stringify(error)
      });
      throw error;
    }
  }, [user, userDetails?.settings_id, updateSettingsMutation, trackEvent]);

  const updateUserDetails = useCallback(async (detailsUpdate: Partial<UserDetails>): Promise<UserDetails | null> => {
    if (!user) {
      if (import.meta.env.DEV) console.error('[DEV] Cannot update details: no user session');
      trackEvent(USER_EVENTS.USER_UPDATE_ERROR, { update: 'user_details', error: 'No user session' });
      return null;
    }

    const affiliateRef = getAffiliateRef();
    // Update affiliate refId only if not already set
    const hasRefUpdate = affiliateRef?.refId && !(userDetails?.eh_usr_meta as EHUserDetailsMeta)?.refId

    try {
      const result = await updateDetailsMutation.mutateAsync({
        ...detailsUpdate,
        ...((hasRefUpdate || detailsUpdate.eh_usr_meta) && {
          eh_usr_meta: {
            ...((userDetails?.eh_usr_meta ?? {}) as EHUserDetailsMeta),
            ...(detailsUpdate.eh_usr_meta && {
              ...(detailsUpdate.eh_usr_meta as EHUserDetailsMeta),
            }),
            ...(hasRefUpdate && {
              refId: affiliateRef.refId,
              refAt: affiliateRef.at
            })
          }
        }),
      });
      return result;
    } catch (error) {
      trackEvent(USER_EVENTS.USER_UPDATE_ERROR, {
        update: 'user_details',
        error: error instanceof Error ? error.message : JSON.stringify(error)
      });
      throw error;
    }
  }, [user, userDetails?.eh_usr_meta, updateDetailsMutation, trackEvent]);

  return {
    user,
    userDetails,
    characters,
    chats,
    settings,
    subscriptions,
    isLoading,
    isUserDetailsLoading,
    error,
    isPastUser,
    isSubscriber,
    isRenewableSubscriber,
    isUnlimited,
    is18Confirmed,
    refetch,
    refetchCharacters,
    refetchUserInfo,
    updateUserDetails,
    updateUserSettings,
  };
} 